/**
 * Search Plugin for ProseMirror
 *
 * Provides text search functionality with visual highlighting of matches.
 * Supports case-sensitive/insensitive search and navigation between matches.
 *
 * Features:
 * - Highlights all matches in the document with decorations
 * - Tracks current match position for navigation
 * - Supports case-sensitive toggle
 * - Provides match count information
 */

import type { Transaction, EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/** Match location in the document */
export interface SearchMatch {
  from: number;
  to: number;
}

/** Plugin state */
interface SearchPluginState {
  /** Current search term */
  searchTerm: string;
  /** Whether search is case-sensitive */
  caseSensitive: boolean;
  /** All matches found in the document */
  matches: SearchMatch[];
  /** Index of the currently selected match (0-based) */
  currentMatchIndex: number;
  /** Decoration set for highlighting matches */
  decorations: DecorationSet;
}

export const searchPluginKey = new PluginKey<SearchPluginState>("search");

/** Meta action types */
type SearchMeta =
  | { type: "setSearch"; term: string; caseSensitive: boolean }
  | { type: "nextMatch" }
  | { type: "prevMatch" }
  | { type: "clear" }
  | { type: "goToMatch"; index: number };

/**
 * Set the search term and options.
 */
export function setSearchTerm(
  state: EditorState,
  term: string,
  caseSensitive = false
): Transaction {
  return state.tr.setMeta(searchPluginKey, {
    type: "setSearch",
    term,
    caseSensitive,
  } as SearchMeta);
}

/**
 * Navigate to the next match.
 */
export function nextSearchMatch(state: EditorState): Transaction {
  return state.tr.setMeta(searchPluginKey, { type: "nextMatch" } as SearchMeta);
}

/**
 * Navigate to the previous match.
 */
export function prevSearchMatch(state: EditorState): Transaction {
  return state.tr.setMeta(searchPluginKey, { type: "prevMatch" } as SearchMeta);
}

/**
 * Clear the search.
 */
export function clearSearch(state: EditorState): Transaction {
  return state.tr.setMeta(searchPluginKey, { type: "clear" } as SearchMeta);
}

/**
 * Go to a specific match by index.
 */
export function goToSearchMatch(
  state: EditorState,
  index: number
): Transaction {
  return state.tr.setMeta(searchPluginKey, {
    type: "goToMatch",
    index,
  } as SearchMeta);
}

/**
 * Get the current search state.
 */
export function getSearchState(state: EditorState): SearchPluginState | null {
  return searchPluginKey.getState(state) ?? null;
}

/**
 * Get the current match count.
 */
export function getMatchCount(state: EditorState): number {
  return searchPluginKey.getState(state)?.matches.length ?? 0;
}

/**
 * Get the current match index (0-based, -1 if no matches).
 */
export function getCurrentMatchIndex(state: EditorState): number {
  const pluginState = searchPluginKey.getState(state);
  if (!pluginState || pluginState.matches.length === 0) return -1;
  return pluginState.currentMatchIndex;
}

/**
 * Get the current match position.
 */
export function getCurrentMatch(state: EditorState): SearchMatch | null {
  const pluginState = searchPluginKey.getState(state);
  if (!pluginState || pluginState.matches.length === 0) return null;
  return pluginState.matches[pluginState.currentMatchIndex] ?? null;
}

/**
 * Find all occurrences of a search term in the document.
 */
function findMatches(
  doc: EditorState["doc"],
  searchTerm: string,
  caseSensitive: boolean
): SearchMatch[] {
  if (!searchTerm) return [];

  const matches: SearchMatch[] = [];
  const searchStr = caseSensitive ? searchTerm : searchTerm.toLowerCase();

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    const text = caseSensitive ? node.text : node.text.toLowerCase();
    let index = 0;

    while ((index = text.indexOf(searchStr, index)) !== -1) {
      matches.push({
        from: pos + index,
        to: pos + index + searchTerm.length,
      });
      index += 1; // Continue searching from next character for overlapping matches
    }
  });

  return matches;
}

/**
 * Create decorations for all matches.
 */
function createDecorations(
  doc: EditorState["doc"],
  matches: SearchMatch[],
  currentMatchIndex: number
): DecorationSet {
  if (matches.length === 0) {
    return DecorationSet.empty;
  }

  const decorations = matches.map((match, index) => {
    const isCurrentMatch = index === currentMatchIndex;
    return Decoration.inline(match.from, match.to, {
      class: isCurrentMatch
        ? "search-match search-match-current"
        : "search-match",
    });
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * Create the search plugin.
 */
export function createSearchPlugin(): Plugin<SearchPluginState> {
  return new Plugin<SearchPluginState>({
    key: searchPluginKey,

    state: {
      init(): SearchPluginState {
        return {
          searchTerm: "",
          caseSensitive: false,
          matches: [],
          currentMatchIndex: 0,
          decorations: DecorationSet.empty,
        };
      },

      apply(tr, pluginState, _oldState, newState): SearchPluginState {
        const meta = tr.getMeta(searchPluginKey) as SearchMeta | undefined;

        if (meta) {
          switch (meta.type) {
            case "setSearch": {
              const matches = findMatches(
                newState.doc,
                meta.term,
                meta.caseSensitive
              );
              const currentMatchIndex = matches.length > 0 ? 0 : 0;
              return {
                searchTerm: meta.term,
                caseSensitive: meta.caseSensitive,
                matches,
                currentMatchIndex,
                decorations: createDecorations(
                  newState.doc,
                  matches,
                  currentMatchIndex
                ),
              };
            }

            case "nextMatch": {
              if (pluginState.matches.length === 0) return pluginState;
              const nextIndex =
                (pluginState.currentMatchIndex + 1) %
                pluginState.matches.length;
              return {
                ...pluginState,
                currentMatchIndex: nextIndex,
                decorations: createDecorations(
                  newState.doc,
                  pluginState.matches,
                  nextIndex
                ),
              };
            }

            case "prevMatch": {
              if (pluginState.matches.length === 0) return pluginState;
              const prevIndex =
                pluginState.currentMatchIndex === 0
                  ? pluginState.matches.length - 1
                  : pluginState.currentMatchIndex - 1;
              return {
                ...pluginState,
                currentMatchIndex: prevIndex,
                decorations: createDecorations(
                  newState.doc,
                  pluginState.matches,
                  prevIndex
                ),
              };
            }

            case "goToMatch": {
              if (
                meta.index < 0 ||
                meta.index >= pluginState.matches.length
              ) {
                return pluginState;
              }
              return {
                ...pluginState,
                currentMatchIndex: meta.index,
                decorations: createDecorations(
                  newState.doc,
                  pluginState.matches,
                  meta.index
                ),
              };
            }

            case "clear": {
              return {
                searchTerm: "",
                caseSensitive: false,
                matches: [],
                currentMatchIndex: 0,
                decorations: DecorationSet.empty,
              };
            }
          }
        }

        // If document changed and we have an active search, re-run it
        if (tr.docChanged && pluginState.searchTerm) {
          const matches = findMatches(
            newState.doc,
            pluginState.searchTerm,
            pluginState.caseSensitive
          );

          // Try to keep the current match position
          let currentMatchIndex = 0;
          if (matches.length > 0 && pluginState.matches.length > 0) {
            const prevMatch = pluginState.matches[pluginState.currentMatchIndex];
            if (prevMatch) {
              // Find the closest match to the previous position
              currentMatchIndex = matches.findIndex(
                (m) =>
                  m.from >= prevMatch.from - 10 && m.from <= prevMatch.from + 10
              );
              if (currentMatchIndex === -1) {
                currentMatchIndex = Math.min(
                  pluginState.currentMatchIndex,
                  matches.length - 1
                );
              }
            }
          }

          return {
            ...pluginState,
            matches,
            currentMatchIndex: Math.max(0, currentMatchIndex),
            decorations: createDecorations(
              newState.doc,
              matches,
              Math.max(0, currentMatchIndex)
            ),
          };
        }

        // Map decorations through document changes
        if (tr.docChanged) {
          return {
            ...pluginState,
            decorations: pluginState.decorations.map(tr.mapping, newState.doc),
          };
        }

        return pluginState;
      },
    },

    props: {
      decorations(state): DecorationSet {
        return searchPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
      },
    },
  });
}

export default createSearchPlugin;
